import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../../context/ThemeContext';
import { API_URL } from '../../utils/api';
import {
  Container, FormBox, Title, FormGroup, Label,
  Input, Button, ErrorMessage, LinkText,
} from './SignIn.styles';

function SignIn({ onLogin }) {
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [noVerificado, setNoVerificado] = useState(false); // true si el error fue email_no_verificado
  const [reenviando,  setReenviando]  = useState(false);
  const [reenviado,   setReenviado]   = useState(false);
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNoVerificado(false);
    setReenviado(false);
    setLoading(true);

    if (!email || !password) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      });

      const data = await res.json();

     if (!res.ok) {
        if (data.error === 'email_no_verificado') {
          setNoVerificado(true);
          setError('Debes verificar tu email antes de iniciar sesión');
        } else if (data.error === 'cuenta_bloqueada') {
          setError(`Cuenta bloqueada por seguridad tras varios intentos fallidos. Espera ${data.minutos} minutos.`);
        } else {
          setError(data.error || 'Email o contraseña incorrectos');
        }
        setLoading(false);
        return;
      }

      localStorage.setItem('trastero_token', data.token);

      onLogin({
        id:                data.usuario.id,
        email:             data.usuario.email,
        name:              data.usuario.nombre,
        avatar:            data.usuario.avatar_url,
        acepta_terminos:   data.usuario.acepta_terminos   || false,
        acepta_privacidad: data.usuario.acepta_privacidad || false,
      });

      navigate('/profile');
    } catch (err) {
      setError('No se pudo conectar al servidor. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleReenviar = async () => {
    setReenviando(true);
    try {
      await fetch(`${API_URL}/api/auth/reenviar-verificacion`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      setReenviado(true);
    } catch {
      // silencioso — anti-enumeración, no revelamos si falló por email inexistente
      setReenviado(true);
    } finally {
      setReenviando(false);
    }
  };

  return (
    <Container bgColor={theme.background}>
      <Helmet>
        <title>Iniciar sesión · Trastero</title>
        <meta name="description" content="Accede a tu cuenta de Trastero para gestionar tus artículos y mensajes." />
      </Helmet>
      <FormBox>
        <Title>Iniciar Sesión</Title>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input
              type="email" id="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com" accentColor={theme.accent}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              type="password" id="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" accentColor={theme.accent}
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {noVerificado && !reenviado && (
            <Button
              type="button"
              disabled={reenviando}
              accentColor={theme.accent}
              onClick={handleReenviar}
              style={{ marginBottom: '12px', opacity: 0.85 }}
            >
              {reenviando ? 'Enviando...' : 'Reenviar email de verificación'}
            </Button>
          )}

          {reenviado && (
            <ErrorMessage style={{ color: theme.accent }}>
              Si el email existe, te hemos enviado un nuevo enlace. Revisa tu bandeja.
            </ErrorMessage>
          )}

          <Button type="submit" disabled={loading} accentColor={theme.accent}>
            {loading ? 'Cargando...' : 'Iniciar Sesión'}
          </Button>
        </form>

        <LinkText accentColor={theme.accent}>
          ¿No tienes cuenta? <a href="/signup">Regístrate aquí</a>
        </LinkText>
      </FormBox>
    </Container>
  );
}

export default SignIn;